import React, { forwardRef, useImperativeHandle } from 'react'
import { SearchLocationProps } from './SearchLocation.types'
import {
  SearchLocationWrapper,
  InputWrapper,
  SearchIconWrapper,
  ClearButton,
  SearchLocationDropdown,
  LocationList,
  LocationItem,
  LocationName,
  LocationTypeTag,
  NoResults,
  LoadingSpinner,
} from './SearchLocation.styles'
import { Input, Icon } from '@popety-io/ui'
import { useLocationSearch } from './hooks/useLocationSearch'
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation'
import { useClickOutside } from './hooks/useClickOutside'
import { formatLocationType, getLocationKey } from './core/locationFormatter'

export const SearchLocation = forwardRef<HTMLInputElement, SearchLocationProps>(
  (
    {
      value = '',
      onChange,
      placeholder = 'Search for an address, city, or region...',
      minSearchLength = 3,
      maxLocations = 20,
      noResultsText = 'No locations found',
      loadingText: _loadingText = 'Loading...',
      searchTypes = ['address', 'municipality', 'land', 'egid', 'egrid', 'npa'],
      onSearch,
      size = 'medium',
      error = false,
      fullWidth = false,
      disabled = false,
      className,
      onLocationSelect,
      autoFocus = false,
      validateSelection,
      resolveLocation,
      enableMapIntegration = true,
      mapAdapterOptions,
      ...props
    },
    ref
  ) => {
    // Main search hook - provides all functionality
    const search = useLocationSearch({
      onSearch,
      searchTypes,
      minSearchLength,
      maxLocations,
      debounceDelay: 300,
      enableMapIntegration,
      mapAdapterOptions,
      onChange,
      onLocationSelect,
      validateSelection,
      resolveLocation,
      initialValue: value,
    })

    // Keyboard navigation
    const keyboard = useKeyboardNavigation({
      isOpen: search.isOpen,
      itemCount: search.locations.length,
      onSelect: (index) => search.handleLocationSelect(search.locations[index]),
      onClose: search.closeDropdown,
    })

    // Click outside to close
    useClickOutside(search.containerRef, search.closeDropdown, search.isOpen)

    // Forward ref to input
    useImperativeHandle(ref, () => search.inputRef.current!, [])

    // Sync input value with prop
    React.useEffect(() => {
      if (value !== search.inputValue) {
        search.setInputValue(value)
      }
    }, [value])

    return (
      <SearchLocationWrapper
        ref={search.containerRef}
        $fullWidth={fullWidth}
        className={className}
      >
        <InputWrapper>
          <SearchIconWrapper>
            <Icon name="search" size="small" />
          </SearchIconWrapper>
          <Input
            ref={search.inputRef}
            type="text"
            value={search.inputValue}
            onChange={search.handleInputChange}
            onFocus={() => {
              if (
                search.inputValue.length >= minSearchLength &&
                search.locations.length > 0
              ) {
                search.openDropdown()
              }
            }}
            placeholder={placeholder}
            size={size}
            error={error || !!search.validationError}
            fullWidth
            disabled={disabled}
            autoFocus={autoFocus}
            {...props}
          />
          {search.inputValue && !disabled && (
            <ClearButton
              onClick={search.handleClear}
              type="button"
              aria-label="Clear location"
            >
              <Icon name="x" size="small" />
            </ClearButton>
          )}
        </InputWrapper>

        <SearchLocationDropdown
          ref={search.dropdownRef}
          $isOpen={search.isOpen}
          role="listbox"
          aria-label="Location suggestions"
        >
          {search.loading || search.isResolving ? (
            <LoadingSpinner>
              <Icon name="settings" size="medium" />
            </LoadingSpinner>
          ) : search.locations.length > 0 ? (
            <LocationList>
              {search.locations.map((location, index) => (
                <LocationItem
                  key={getLocationKey(location)}
                  onClick={() => search.handleLocationSelect(location)}
                  onMouseEnter={() => keyboard.setHighlightedIndex(index)}
                  $isHighlighted={index === keyboard.highlightedIndex}
                  role="option"
                  aria-selected={index === keyboard.highlightedIndex}
                >
                  <LocationName>{location.location_search}</LocationName>
                  <LocationTypeTag $type={location.location_type}>
                    {formatLocationType(location.location_type)}
                  </LocationTypeTag>
                </LocationItem>
              ))}
            </LocationList>
          ) : search.inputValue.length >= minSearchLength ? (
            <NoResults>{search.validationError || noResultsText}</NoResults>
          ) : null}
        </SearchLocationDropdown>
      </SearchLocationWrapper>
    )
  }
)

SearchLocation.displayName = 'SearchLocation'
